import { jsPDF } from 'jspdf';

export interface ExportableData {
    headers: string[];
    rows: (string | number)[][];
    fileName: string;
}

export interface PropertyForPDF {
    id: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    county: string;
    status?: string;
    auctionDate?: string;
    openingBid?: number;
    beds?: number;
    baths?: number;
    sqft?: number;
    yearBuilt?: number;
    propertyType?: string;
    ownerName?: string;
    assessedValue?: number;
    marketValue?: number;
    locationConfidence?: string;
    locationType?: string;
    compsMedian?: number;
    deltaPercent?: number;
    lat?: number;
    lng?: number;
}

export interface KpiData {
    id: string;
    name: string;
    value: number;
    target?: number;
    unit: string;
    trend?: 'up' | 'down' | 'stable';
    timestamp: string;
}

export interface TicketData {
    id: string;
    title: string;
    status: string;
    priority: string;
    assignee?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ActivityData {
    id: string;
    type: string;
    description: string;
    user: string;
    timestamp: string;
    metadata?: Record<string, unknown>;
}

export interface NotificationData {
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    read: boolean;
    timestamp: string;
}

export const exportToCSV = (data: ExportableData): void => {
    const csvContent = [
        data.headers.join(','),
        ...data.rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${data.fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportToJSON = (data: Record<string, unknown>, fileName: string): void => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportKpiData = (kpis: KpiData[]): void => {
    const data: ExportableData = {
        headers: ['Metric', 'Value', 'Delta', 'Target', 'Status'],
        rows: kpis.map(kpi => [
            kpi.label,
            `${kpi.value}${kpi.suffix || ''}`,
            `${kpi.delta > 0 ? '+' : ''}${kpi.delta}%`,
            kpi.target ? `${kpi.target}${kpi.suffix || ''}` : 'N/A',
            kpi.intent,
        ]),
        fileName: `kpi-dashboard-${new Date().toISOString().split('T')[0]}`,
    };

    exportToCSV(data);
};

export const exportTicketData = (tickets: TicketData[]): void => {
    const data: ExportableData = {
        headers: ['ID', 'Title', 'Status', 'Priority', 'Assignee', 'Created', 'Updated'],
        rows: tickets.map(ticket => [
            ticket.id,
            ticket.title,
            ticket.status,
            ticket.priority,
            ticket.assignee,
            ticket.createdAt?.toLocaleDateString() || 'N/A',
            ticket.updatedAt?.toLocaleDateString() || 'N/A',
        ]),
        fileName: `support-tickets-${new Date().toISOString().split('T')[0]}`,
    };

    exportToCSV(data);
};

export const exportActivityData = (activities: ActivityData[]): void => {
    const data: ExportableData = {
        headers: ['Timestamp', 'Type', 'Actor', 'Action', 'Target', 'Severity'],
        rows: activities.map(activity => [
            activity.timestamp?.toISOString() || 'N/A',
            activity.type,
            activity.actor?.name || 'Unknown',
            activity.action,
            activity.target?.name || 'N/A',
            activity.severity,
        ]),
        fileName: `activity-log-${new Date().toISOString().split('T')[0]}`,
    };

    exportToCSV(data);
};

export const exportNotificationData = (notifications: NotificationData[]): void => {
    const data: ExportableData = {
        headers: ['Title', 'Type', 'Priority', 'Source', 'Timestamp', 'Read'],
        rows: notifications.map(notification => [
            notification.title,
            notification.type,
            notification.priority,
            notification.source,
            notification.timestamp?.toISOString() || 'N/A',
            notification.read ? 'Yes' : 'No',
        ]),
        fileName: `notifications-${new Date().toISOString().split('T')[0]}`,
    };

    exportToCSV(data);
};

export interface PDFExportOptions {
    properties: PropertyForPDF[];
    county: string;
    exportDate?: string;
}

const formatCurrencyForPDF = (value: number | undefined): string => {
    if (value === undefined || !Number.isFinite(value)) return 'TBD';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

const sanitizeForPDF = (value: string | number | undefined | null, fallback = 'TBD'): string => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return fallback;
        return value.toString();
    }
    const str = String(value).trim();
    if (str === '' || str === 'undefined' || str === 'null' || str === 'NaN') {
        return fallback;
    }
    return str;
};

export const exportToPDF = (options: PDFExportOptions): void => {
    const { properties, county, exportDate = new Date().toISOString() } = options;

    if (properties.length === 0) return;

    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;

    // Cover Page
    doc.setFillColor(30, 41, 59); // Dark slate
    doc.rect(0, 0, pageWidth, 60, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('RealSignal', margin, 30);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Investor Deal Packet', margin, 42);

    doc.setTextColor(51, 51, 51);
    doc.setFontSize(12);
    let y = 80;

    doc.setFont('helvetica', 'bold');
    doc.text('Export Summary', margin, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    const summaryData = [
        ['County:', county.charAt(0).toUpperCase() + county.slice(1)],
        ['Total Properties:', properties.length.toString()],
        [
            'Export Date:',
            new Date(exportDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }),
        ],
    ];

    summaryData.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.text(value, margin + 45, y);
        y += 7;
    });

    // Summary table of all properties
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Properties Overview', margin, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    // Table header
    const colWidths = [70, 35, 35, 40];
    const headers = ['Address', 'Status', 'Auction Date', 'Opening Bid'];

    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y - 4, contentWidth, 7, 'F');

    let xPos = margin;
    headers.forEach((header, i) => {
        doc.text(header, xPos + 2, y);
        xPos += colWidths[i];
    });
    y += 8;

    doc.setFont('helvetica', 'normal');

    // Table rows (limit to first 15 on cover page)
    const coverProperties = properties.slice(0, 15);
    coverProperties.forEach((prop, index) => {
        if (y > pageHeight - 30) return; // Stop if near bottom

        if (index % 2 === 1) {
            doc.setFillColor(250, 250, 250);
            doc.rect(margin, y - 4, contentWidth, 6, 'F');
        }

        xPos = margin;
        const rowData = [
            (prop.address || 'TBD').substring(0, 35),
            sanitizeForPDF(prop.status),
            sanitizeForPDF(prop.auctionDate),
            formatCurrencyForPDF(prop.openingBid),
        ];

        rowData.forEach((cell, i) => {
            doc.text(cell, xPos + 2, y);
            xPos += colWidths[i];
        });
        y += 6;
    });

    if (properties.length > 15) {
        y += 5;
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text(
            `... and ${properties.length - 15} more properties (see individual pages)`,
            margin,
            y
        );
        doc.setTextColor(51, 51, 51);
    }

    // Footer on cover page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Generated by RealSignal | For investor use only', margin, pageHeight - 10);
    doc.text(`Page 1 of ${properties.length + 1}`, pageWidth - margin - 25, pageHeight - 10);

    // Individual property pages
    properties.forEach((prop, index) => {
        doc.addPage();

        // Header bar
        doc.setFillColor(30, 41, 59);
        doc.rect(0, 0, pageWidth, 25, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('RealSignal Property Report', margin, 15);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Property ${index + 1} of ${properties.length}`, pageWidth - margin - 35, 15);

        let y = 35;
        doc.setTextColor(51, 51, 51);

        // Address section
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(sanitizeForPDF(prop.address, 'Address Not Available'), margin, y);
        y += 7;

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const cityStateZip =
            `${sanitizeForPDF(prop.city)}, ${sanitizeForPDF(prop.state, 'TX')} ${sanitizeForPDF(prop.zip, '')}`.trim();
        doc.text(cityStateZip, margin, y);
        y += 5;
        doc.text(`${sanitizeForPDF(prop.county, 'Harris')} County`, margin, y);
        y += 12;

        doc.setTextColor(51, 51, 51);

        // Status & Auction Info Box
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, y, contentWidth, 30, 3, 3, 'F');

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Foreclosure Status', margin + 5, y + 8);
        doc.setFont('helvetica', 'normal');
        doc.text(sanitizeForPDF(prop.status), margin + 5, y + 15);

        doc.setFont('helvetica', 'bold');
        doc.text('Auction Date', margin + 55, y + 8);
        doc.setFont('helvetica', 'normal');
        doc.text(sanitizeForPDF(prop.auctionDate), margin + 55, y + 15);

        doc.setFont('helvetica', 'bold');
        doc.text('Opening Bid', margin + 110, y + 8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.text(formatCurrencyForPDF(prop.openingBid), margin + 110, y + 16);

        y += 38;

        // Property Details Section
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Property Details', margin, y);
        y += 8;

        doc.setFontSize(10);
        const detailsLeft = [
            ['Beds:', sanitizeForPDF(prop.beds)],
            ['Baths:', sanitizeForPDF(prop.baths)],
            ['Sq Ft:', sanitizeForPDF(prop.sqft)],
            ['Year Built:', sanitizeForPDF(prop.yearBuilt)],
        ];

        const detailsRight = [
            ['Property Type:', sanitizeForPDF(prop.propertyType)],
            ['Owner:', sanitizeForPDF(prop.ownerName)],
            ['Assessed Value:', formatCurrencyForPDF(prop.assessedValue)],
            ['Market Value:', formatCurrencyForPDF(prop.marketValue)],
        ];

        detailsLeft.forEach(([label, value], i) => {
            doc.setFont('helvetica', 'bold');
            doc.text(label, margin, y + i * 7);
            doc.setFont('helvetica', 'normal');
            doc.text(value, margin + 25, y + i * 7);
        });

        detailsRight.forEach(([label, value], i) => {
            doc.setFont('helvetica', 'bold');
            doc.text(label, margin + 90, y + i * 7);
            doc.setFont('helvetica', 'normal');
            doc.text(value, margin + 130, y + i * 7);
        });

        y += 35;

        // Valuation Analysis (if comps data available)
        if (prop.compsMedian !== undefined && prop.openingBid !== undefined) {
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('Valuation Analysis', margin, y);
            y += 8;

            doc.setFontSize(10);
            const valuationData = [
                ['Comps Median:', formatCurrencyForPDF(prop.compsMedian)],
                ['Opening Bid:', formatCurrencyForPDF(prop.openingBid)],
                [
                    'Delta:',
                    prop.deltaPercent !== undefined ? `${prop.deltaPercent.toFixed(1)}%` : 'TBD',
                ],
            ];

            valuationData.forEach(([label, value], i) => {
                doc.setFont('helvetica', 'bold');
                doc.text(label, margin, y + i * 7);
                doc.setFont('helvetica', 'normal');
                doc.text(value, margin + 35, y + i * 7);
            });

            y += 28;
        }

        // Location Quality
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Location Data', margin, y);
        y += 8;

        doc.setFontSize(10);
        const locationQuality =
            prop.locationType === 'rooftop'
                ? 'Rooftop (High)'
                : prop.locationType === 'parcel_polygon'
                  ? 'Parcel Centroid'
                  : prop.locationConfidence === 'high'
                    ? 'High Confidence'
                    : prop.locationConfidence === 'low'
                      ? 'Approximate'
                      : 'TBD';

        doc.setFont('helvetica', 'bold');
        doc.text('Location Quality:', margin, y);
        doc.setFont('helvetica', 'normal');
        doc.text(locationQuality, margin + 40, y);
        y += 7;

        if (prop.lat && prop.lng) {
            doc.setFont('helvetica', 'bold');
            doc.text('Coordinates:', margin, y);
            doc.setFont('helvetica', 'normal');
            doc.text(`${prop.lat.toFixed(6)}, ${prop.lng.toFixed(6)}`, margin + 40, y);
            y += 7;

            // Google Maps link
            const mapsUrl = `https://www.google.com/maps?q=${prop.lat},${prop.lng}`;
            doc.setFont('helvetica', 'bold');
            doc.text('Map Link:', margin, y);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 102, 204);
            doc.textWithLink(mapsUrl.substring(0, 60) + '...', margin + 40, y, { url: mapsUrl });
            doc.setTextColor(51, 51, 51);
            y += 12;
        }

        // Notes section (blank for user to fill in)
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes', margin, y);
        y += 5;

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        for (let i = 0; i < 5; i++) {
            doc.line(margin, y + i * 8, pageWidth - margin, y + i * 8);
        }

        // Footer with provenance
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.setFont('helvetica', 'normal');
        doc.text(`Property ID: ${prop.id}`, margin, pageHeight - 18);
        doc.text(
            `Source: Harris County Tax Office | Generated: ${new Date(exportDate).toLocaleString()}`,
            margin,
            pageHeight - 12
        );
        doc.text(
            `Page ${index + 2} of ${properties.length + 1}`,
            pageWidth - margin - 25,
            pageHeight - 12
        );
    });

    // Save the PDF
    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`realsignal_${county.toLowerCase()}_tearsheet_${dateStr}.pdf`);
};
